import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { PencilSimple, Trash, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { eur } from './QuoteDetails';
import { ItemsEditor, VatTotals, emptyItem, itemsInvalid, cleanItems, subtotalOf } from './QuoteItemsEditor';

const blank = () => ({ name: '', items: [emptyItem()], vat_enabled: false, vat_rate: 22, notes: '', valid_days: 15 });

const TemplateEditor = ({ initial, onSaved, onBack }) => {
  const [t, setT] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setT({ ...t, [k]: v });
  const save = async () => {
    if (!t.name.trim()) return toast.error('Dai un nome al modello');
    if (itemsInvalid(t.items)) return toast.error('Completa tutte le voci');
    setSaving(true);
    const body = { ...t, items: cleanItems(t.items), vat_rate: Number(t.vat_rate || 0), valid_days: Number(t.valid_days || 15) };
    try {
      if (t.template_id) await api.put(`/api/quotes/templates/${t.template_id}`, body);
      else await api.post('/api/quotes/templates', body);
      toast.success('Modello salvato'); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Controlla i dati del modello');
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="template-editor">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1 sm:col-span-2"><Label>Nome del modello</Label>
          <Input value={t.name} onChange={(e) => set('name', e.target.value)} placeholder="Es. Assistenza diurna 10 ore" data-testid="template-name-input" /></div>
        <div className="space-y-1"><Label>Validità (giorni)</Label>
          <Input type="number" min="1" max="365" value={t.valid_days} onChange={(e) => set('valid_days', e.target.value)} data-testid="template-valid-days-input" /></div>
      </div>
      <ItemsEditor items={t.items} setItems={(v) => set('items', v)} prefix="template" />
      <VatTotals items={t.items} vatEnabled={t.vat_enabled} setVatEnabled={(v) => set('vat_enabled', v)}
        vatRate={t.vat_rate} setVatRate={(v) => set('vat_rate', v)} prefix="template" />
      <div className="space-y-1"><Label>Note</Label>
        <Textarea rows={2} value={t.notes} onChange={(e) => set('notes', e.target.value)} data-testid="template-notes-input" /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onBack} data-testid="template-back-btn">Indietro</Button>
        <Button onClick={save} disabled={saving} data-testid="template-save-btn">{saving ? 'Salvataggio…' : 'Salva modello'}</Button>
      </div>
    </div>
  );
};

const QuoteTemplatesDialog = ({ open, onOpenChange }) => {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get('/api/quotes/templates').then((r) => setTemplates(r.data.items || [])).catch(() => toast.error('Errore nel caricamento dei modelli'));
  useEffect(() => { if (open) { setEditing(null); load(); } }, [open]);

  const remove = async (t) => {
    if (!window.confirm(`Eliminare il modello «${t.name}»?`)) return;
    try { await api.delete(`/api/quotes/templates/${t.template_id}`); toast.success('Modello eliminato'); load(); }
    catch { toast.error('Eliminazione non riuscita'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-templates-dialog">
        <DialogHeader>
          <DialogTitle>Modelli di preventivo</DialogTitle>
          <DialogDescription>Salva le tue prestazioni e i prezzi abituali per comporre i preventivi in pochi secondi.</DialogDescription>
        </DialogHeader>
        {editing ? (
          <TemplateEditor initial={editing} onBack={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : (
          <div className="space-y-3">
            {templates.length === 0 && <p className="text-sm text-slate-500" data-testid="templates-empty">Nessun modello salvato.</p>}
            {templates.map((t) => (
              <div key={t.template_id} className="flex items-center gap-3 rounded-xl border border-[#E3F2FD] p-3" data-testid={`template-row-${t.template_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.items.length} voci · {eur(subtotalOf(t.items))}{t.vat_enabled ? ` + IVA ${t.vat_rate}%` : ''} · validità {t.valid_days} giorni</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditing({ ...t })} data-testid={`template-edit-${t.template_id}`}><PencilSimple size={16} /></Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(t)} data-testid={`template-delete-${t.template_id}`}><Trash size={16} /></Button>
              </div>
            ))}
            <Button onClick={() => setEditing(blank())} className="rounded-full" data-testid="template-new-btn"><Plus size={16} className="mr-1" /> Nuovo modello</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteTemplatesDialog;
